/**
 * chain.js
 *
 * Network configuration for Arc (Circle's stablecoin L1).
 *
 * ── SWITCHING TO MAINNET ────────────────────────────────────────────
 * Change ACTIVE_NETWORK below to 'mainnet', or set
 * REACT_APP_NETWORK=mainnet at build time. Everything else in the app
 * reads from here, so nothing else needs editing.
 *
 * Arc mainnet (chain 5042) went live 16 September 2026 and its values
 * are filled in below, taken from Circle's own docs.arc.io and
 * cross-checked against several independent integrations.
 *
 * Two traps worth knowing, because both are still circulating:
 *   - Some chain registries list an unrelated network as "ARC Mainnet"
 *     with a native symbol of "ARC". That is NOT this chain. Circle's
 *     Arc pays gas in USDC and has no ARC token at all, so any entry
 *     showing an ARC currency is a different network entirely.
 *   - The same confusion hit testnet, where 1516 circulated widely
 *     before 5042002 was confirmed.
 *
 * Verify against a node before launch — see the curl at the bottom of
 * this file. The node's own answer is the only authority.
 */

const NETWORKS = {
  testnet: {
    key: 'testnet',
    chainIdDec: 5042002,
    chainIdHex: '0x4cef52',
    chainName: 'Arc Testnet',
    shortName: 'ARC TESTNET',
    isTestnet: true,
    nativeCurrency: { name: 'USDC', symbol: 'USDC', decimals: 18 },
    rpcUrls: ['https://rpc.testnet.arc.network'],
    blockExplorerUrls: ['https://testnet.arcscan.app'],
    openSeaAssetBase: 'https://testnets.opensea.io/assets/arc-testnet',
    openSeaCollection: '',
  },

  mainnet: {
    key: 'mainnet',
    // Verified against Circle's own docs (docs.arc.io "Connect to Arc"),
    // and independently corroborated by Chainstack (citing Circle's
    // GitHub release notes), Bitquery's Arc mainnet reference, and two
    // wallet integrations. Arc mainnet opened 16 September 2026.
    chainIdDec: 5042,
    chainIdHex: '0x13b2',        // 5042
    chainName: 'Arc',
    shortName: 'ARC',
    isTestnet: false,
    // USDC is the native gas token. The NATIVE interface uses 18
    // decimals; the separate USDC ERC-20 precompile at
    // 0x3600000000000000000000000000000000000000 uses 6. This contract
    // takes payment via msg.value — the native path — so 18 is correct
    // and 1.5 USDC is 1500000000000000000.
    nativeCurrency: { name: 'USDC', symbol: 'USDC', decimals: 18 },
    rpcUrls: ['https://rpc.mainnet.arc.io'],
    blockExplorerUrls: ['https://explorer.arc.io'],
    openSeaAssetBase: 'https://opensea.io/assets/arc',
    openSeaCollection: 'https://opensea.io/collection/arc-hexabots-254746558',
  },
};

const requested = (process.env.REACT_APP_NETWORK || 'testnet').toLowerCase();
const chosen = NETWORKS[requested] || NETWORKS.testnet;

// Refuse to run half-configured: an entry missing a chain ID or RPC
// would leave the app unable to validate the user's network at all,
// which is worse than staying on testnet.
if (chosen.key === 'mainnet' && (!chosen.chainIdDec || chosen.rpcUrls.length === 0)) {
  console.error(
    '[chain] REACT_APP_NETWORK=mainnet but Arc mainnet chainId/RPC are not set in src/lib/chain.js. ' +
    'Falling back to testnet. Fill the mainnet block with values from arc.io before launching.'
  );
}

export const ACTIVE_NETWORK =
  chosen.key === 'mainnet' && chosen.chainIdDec && chosen.rpcUrls.length
    ? chosen
    : NETWORKS.testnet;

/** Kept as the exported name the rest of the app already imports. */
export const ARC_TESTNET = ACTIVE_NETWORK;
export const ARC_NETWORK = ACTIVE_NETWORK;

export const NATIVE_DECIMALS = ACTIVE_NETWORK.nativeCurrency.decimals;
export const NATIVE_SYMBOL = ACTIVE_NETWORK.nativeCurrency.symbol;
export const IS_TESTNET = ACTIVE_NETWORK.isTestnet;
export const OPENSEA_ASSET_BASE =
  process.env.REACT_APP_OPENSEA_BASE || ACTIVE_NETWORK.openSeaAssetBase;

/** Collection landing page on OpenSea. */
export const OPENSEA_COLLECTION =
  process.env.REACT_APP_OPENSEA_COLLECTION || ACTIVE_NETWORK.openSeaCollection || '';

/**
 * Read-only RPC endpoints, tried in order.
 *
 * Put a dedicated endpoint first via REACT_APP_ARC_RPC_URL before a real
 * mint — a shared public endpoint rate-limits under launch traffic, and
 * when it does the supply counter freezes for everyone.
 */
export const READ_RPC_URLS = [
  process.env.REACT_APP_ARC_RPC_URL,
  ...ACTIVE_NETWORK.rpcUrls,
].filter(Boolean);

/** Prompts the wallet to add (if unknown) and switch to the active Arc network. */
export async function ensureArcNetwork(ethereumProvider) {
  if (!ethereumProvider) throw new Error('No wallet provider.');
  const net = ACTIVE_NETWORK;
  try {
    await ethereumProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: net.chainIdHex }],
    });
  } catch (switchError) {
    // 4902 = chain unknown to this wallet; 4901 = not connected to it
    if (switchError?.code === 4902 || switchError?.code === -32603) {
      await ethereumProvider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: net.chainIdHex,
          chainName: net.chainName,
          nativeCurrency: net.nativeCurrency,
          rpcUrls: net.rpcUrls,
          blockExplorerUrls: net.blockExplorerUrls,
        }],
      });
      // Adding does not always switch — ask again.
      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: net.chainIdHex }],
      });
    } else {
      throw switchError;
    }
  }
}

// Backwards-compatible alias
export const ensureArcTestnet = ensureArcNetwork;

/*
 * Verify a chain ID before trusting it:
 *
 *   curl -X POST <RPC_URL> -H "Content-Type: application/json" \
 *     -d '{"jsonrpc":"2.0","method":"eth_chainId","params":[],"id":1}'
 *
 * The value the node returns is authoritative. Anything a blog, chain
 * registry or wallet guide claims is not.
 */
